import { For, Show, createMemo } from 'solid-js';
import type { SelectionContext } from '../selection';
import { Mode, isUserFacing } from '../../project/PosterMode';
import { interpolateTemplate, textRendersEmpty } from '../text/BaseTextObject';
import { SelectableObject } from '../interactions/SelectableObject';
import { IRenderContext, Object } from '../Object';
import { Placeholder } from '../Placeholder';
import type { RuntimeObject, RuntimeText, DataRecord } from '../../types';
import { ContainerImpl } from 'Shared/posters/canvas/container/ContainerImpl';
import { ShapeRect } from 'Shared/posters/canvas/RoundedRect';
import { createStore } from 'solid-js/store';
import { GeometrySeed, adoptDesignGeometry, defaultSeedBox, type Dimensions } from 'Shared/posters/canvas/interactions/ObjectImpl';
import { cloneBox, type Box } from '../../types';
import { useUser } from 'Shared/SiteProvider';

// ── Child outlines (editing aid) ──────────────────────────────────────────────
//
// When a container is selected its immediate text and container children get a thin, faint outline
// on their boundary — fainter than the main selection border, and purely a visual aid (no pointer
// events / behaviour). Empty text renders as a shaded placeholder block and is skipped; images are
// never outlined. Only the container's own direct children are drawn here, so grandchildren stay
// un-outlined.

function isOutlinedChild(obj: RuntimeObject, record: DataRecord, useUser: boolean): boolean {
	if (obj.type === 'container') return true
	if (obj.type !== 'text') return false
	const text = obj as RuntimeText
	const raw  = text.content ?? ''
	const content = text.style?.isField ? interpolateTemplate(raw, record, useUser) : raw
	return content !== ''
}

// Drawn in container-local space (inside the container's content <g>), so each child's getHitBBox —
// itself relative to the container origin — maps straight onto the rect.
function ChildOutlines(P: { objects: RuntimeObject[]; record: DataRecord, mode:Mode, selection: SelectionContext })
{
	const outlined = createMemo(() => P.objects.filter(obj => isOutlinedChild(obj, P.record, isUserFacing(P.mode))));

	return <For each={outlined()}>{obj => <ChildOutline {...obj} selection={P.selection} />}</For>;
}

function ChildOutline(P)
{
	const userCtx = useUser();
	const box = createMemo(() => P.selection.impls[P.id]?.getHitBBox?.(!!userCtx));

	return (
		<Show when={box()}>
			<rect
				x={box()!.pos.x} y={box()!.pos.y}
				width={box()!.dims.width} height={box()!.dims.height}
				fill="none"
				stroke="#4d9eff"
				stroke-opacity="0.3"
				stroke-width="1"
				pointer-events="none"
			/>
		</Show>
	);
}

// ── ContainerObject ───────────────────────────────────────────────────────────

export function ContainerObject(P: IRenderContext)
{
	const bgRef: { current: SVGElement | null } = { current: null };

	const seed = (): GeometrySeed => ({
		box: {
			pos:  P.object.position ?? defaultSeedBox.pos,
			dims: {
				width:  P.object.size?.width  ?? defaultSeedBox.dims.width,
				height: P.object.size?.height ?? defaultSeedBox.dims.height,
			},
		},
		layoutPos: P.layoutPos,
	});

	const [pos, setPos] = createStore(cloneBox(seed().box));
	const computingPositions = { current: false };

	// The design geometry this store was last seeded/adopted from — see adoptDesignGeometry. A
	// container's seed carries no measurement, so it doubles as the design box.
	const adopted: { current: Box | null } = { current: null };

	const impl = createMemo(() => {
		const g = seed();
		const created = new ContainerImpl(pos, setPos, g,
			P.object, P.canvas, P.record, P.theme, P.parentOffset, bgRef,
			() => isUserFacing(P.mode),
			(id: string) => P.selection.impls[id],
			computingPositions,
		);
		adoptDesignGeometry(created, g.box, g, adopted);
		return created;
	});

	return (
		<SelectableObject
			objects={P.objects}
			selection={P.selection}
			history={P.history}
			drag={P.drag}
			impl={impl()}
			canvas={P.canvas}
			design={P.design}
			parentOffset={P.parentOffset}
			parentId={P.parentId}
			mode={P.mode}
		>
			{(dims: () => Dimensions) => (
				<ContainerObjectBody
					objects={P.objects}
					selection={P.selection}
					history={P.history}
					drag={P.drag}
					images={P.images}
					upload={P.upload}
					design={P.design}
					dims={dims()}
					impl={impl()}
					object={P.object}
					record={P.record}
					theme={P.theme}
					textRefs={P.textRefs}
					mode={P.mode}
				/>
			)}
		</SelectableObject>
	)
}

function ContainerObjectBody(P)
{
	const dragCtx = P.drag;
	const selCtx  = P.selection;
	const userCtx = useUser();

	const visibleObjects = createMemo((): RuntimeObject[] => {
		const objs = P.object.objects ?? [];
		return isUserFacing(P.mode) ? objs.filter(o => !textRendersEmpty(o, P.record)) : objs;
	});

	const shape = createMemo(() => P.impl.shapeAttrs());
	const corners = createMemo(() => P.impl.corners());
	const parentOffset = createMemo(() => P.impl.nestedParentOffset(!!userCtx));
	const childPositions = createMemo(() => P.impl.childPositions(dragCtx));

	let contentRef: SVGGElement | undefined;
	const bgRef: { current: SVGElement | null } = { current: null };

	return (<>
		<Show
			when={P.impl.isCircle()}
			fallback={
				<ShapeRect
					width={P.dims.width} height={P.dims.height}
					corners={corners()}
					shape={shape()}
					setEl={el => bgRef.current = el}
				/>
			}
		>
			<circle
				ref={el => bgRef.current = el}
				cx={P.dims.width / 2} cy={P.dims.height / 2}
				r={Math.min(P.dims.width, P.dims.height) / 2}
				fill={shape().fill}
				stroke={shape().stroke ?? 'none'}
				stroke-width={shape().strokeWidth}
				style={shape().filter ? { filter: shape().filter } : {}}
			/>
		</Show>

		<Show when={!P.impl.getHasFill() && !P.impl.getHasBorder() && (P.object?.objects ?? []).length === 0}>
			<Placeholder id={P.object.id} width={P.dims.width} height={P.dims.height} />
		</Show>

		<g ref={el => contentRef = el}>
			<For each={visibleObjects()}>{obj => <Object
					objects={P.objects}
					selection={P.selection}
					history={P.history}
					drag={dragCtx}
					images={P.images}
					upload={P.upload}
					design={P.design}
					record={P.record}
					theme={P.theme}
					textRefs={P.textRefs}
					canvas={P.dims}
					parentOffset={() => parentOffset()}
					parentId={P.object.id}
					layoutPos={() => P.impl.isComputingPositions() ? P.impl.inFlightChildPosition(obj) : childPositions()[obj.id]}
					object={obj}
					mode={P.mode}
				/>}
			</For>
			<Show when={selCtx && selCtx.selectedIds().has(P.object.id)}>
				<ChildOutlines objects={visibleObjects()} record={P.record} mode={P.mode} selection={selCtx} />
			</Show>
		</g>

		<Show when={selCtx && P.impl.anyChildSelected(selCtx)}>
			<rect
				x="0" y="0"
				width={P.dims.width} height={P.dims.height}
				fill="none"
				stroke="#4d9eff"
				stroke-opacity="0.3"
				stroke-width="1"
				pointer-events="none"
			/>
		</Show>
	</>);
}

